import { createClient } from "@prismicio/client"
import { localStorage } from "@prismicio/editor-support/DOM"
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react"
import { z } from "zod"

import { type RequestRunReturnType, Requests, RequestTypes } from "./Request"

const APIExplorerRequestProperties = z.object({
  repository: z.object({
    id: z.string(),
    accessToken: z.string().optional(),
  }),
  type: z.enum(RequestTypes),
  options: z.record(z.enum(RequestTypes), z.record(z.string()).optional()),
  pagination: z.object({
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
  lang: z.string().optional(),
  ref: z.string().optional(),
})

export type APIExplorerRequestProperties = z.infer<
  typeof APIExplorerRequestProperties
>

type APIExplorerRequestMethods = {
  setRepositoryID: (id: string) => void
  setRepositoryAccessToken: (token: string) => void
  setRequestType: (type: RequestTypes) => void
  setRequestOptions: <Type extends RequestTypes>(
    type: Type,
    options: APIExplorerRequestProperties["options"][Type],
  ) => void
  setPagination: (pagination: { page?: number; pageSize?: number }) => void
  setLang: (lang?: string) => void
  setRef: (ref?: string) => void
  run: () => Promise<void>
}

type ResponseBase = { duration: number }
type ResponseIdle = {
  status: "idle"
}
type ResponsePending = {
  status: "pending"
}
export type ResponseSuccess = ResponseBase & {
  status: "success"
  url?: string
  ref?: string
  data: RequestRunReturnType
}
export type ResponseError = ResponseBase & {
  status: "error"
  url?: string
  error: Error
}

type APIExplorerResponse =
  | ResponseIdle
  | ResponsePending
  | ResponseSuccess
  | ResponseError

type APIExplorerConfig = {
  /**
   * Repository information to use the API Explorer with.
   * When not provided, the API Explorer will allow entering a repository ID.
   */
  repository?: {
    id: string
    documentAPIEndpoint: string
  }
}

type APIExplorerContextType = {
  request: APIExplorerRequestProperties & APIExplorerRequestMethods
  response: APIExplorerResponse
  config: APIExplorerConfig
}

const APIExplorerContext = createContext<APIExplorerContextType | undefined>(
  undefined,
)

export type APIExplorerEvents =
  | [
      event: "API Explorer Request",
      args: {
        type: RequestTypes
        repositoryID: string
      },
    ]
  | [
      event: "API Explorer Request Error",
      args: {
        type: RequestTypes
        repositoryID: string
        error?: string
        url?: string
      },
    ]

export type APIExplorerProviderProps = {
  track?: (...params: APIExplorerEvents) => void
} & APIExplorerConfig &
  PropsWithChildren

export const APIExplorerProvider = (props: APIExplorerProviderProps) => {
  const { track = () => {}, children, ...config } = props

  const [requestProperties, setRequestProperties] = useState(
    getInitialRequestProperties(config.repository),
  )

  const requestMethods: APIExplorerRequestMethods = {
    setRepositoryID(id: string) {
      setRequestProperties((prev) => ({
        ...prev,
        repository: {
          ...prev.repository,
          id,
        },
      }))
    },
    setRepositoryAccessToken(token: string) {
      setRequestProperties((prev) => ({
        ...prev,
        repository: {
          ...prev.repository,
          accessToken: token,
        },
      }))
    },
    setRequestType(type: RequestTypes) {
      setRequestProperties((prev) => ({
        ...prev,
        type,
      }))
    },
    setRequestOptions(type, options) {
      setRequestProperties((prev) => ({
        ...prev,
        options: {
          ...prev.options,
          [type]: options,
        },
      }))
    },
    setPagination(pagination) {
      setRequestProperties((prev) => ({
        ...prev,
        pagination,
      }))
    },
    setLang(lang) {
      setRequestProperties((prev) => ({
        ...prev,
        lang: lang || undefined,
      }))
    },
    setRef(ref) {
      setRequestProperties((prev) => ({
        ...prev,
        ref: ref || undefined,
      }))
    },
    async run() {
      setResponse({ status: "pending" })
      track("API Explorer Request", {
        type: request.type,
        repositoryID: request.repository.id,
      })

      let ref: string | undefined
      let url: string | undefined

      const client = createClient(requestProperties.repository.id, {
        documentAPIEndpoint: config.repository?.documentAPIEndpoint,
        accessToken: requestProperties.repository.accessToken,
        fetch(input, init) {
          const urlObject = new URL(input)
          ref = urlObject.searchParams.get("ref") ?? undefined
          url = input
          return fetch(input, init)
        },
      })

      const start = Date.now()

      try {
        const data = await Requests[requestProperties.type].run(client, {
          ...requestProperties.options[requestProperties.type],
          ...requestProperties.pagination,
          lang: request.lang,
          ref: request.ref,
        })
        setResponse({
          status: "success",
          duration: Date.now() - start,
          ref,
          url,
          data,
        })
      } catch (rawError) {
        const error =
          rawError instanceof Error ? rawError : new Error("Unknown error")
        setResponse({
          status: "error",
          duration: Date.now() - start,
          url,
          error,
        })
        track("API Explorer Request Error", {
          type: request.type,
          repositoryID: request.repository.id,
          url,
          error: error.message,
        })
      }
    },
  }

  const request = {
    ...requestProperties,
    ...requestMethods,
  }

  const [response, setResponse] = useState<APIExplorerResponse>({
    status: "idle",
  })

  // Run request on load to get some initial content
  useEffect(() => {
    void request.run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Preserve state in local storage
  useEffect(() => {
    localStorage.write(
      apiExplorerLocalStorageKey,
      JSON.stringify(requestProperties),
    )
  }, [requestProperties])

  return (
    <APIExplorerContext.Provider value={{ request, response, config }}>
      {children}
    </APIExplorerContext.Provider>
  )
}

export function useAPIExplorer(): APIExplorerContextType {
  const context = useContext(APIExplorerContext)

  if (!context) {
    throw new Error("useAPIExplorer must be used within an APIExplorerProvider")
  }

  return context
}

export const apiExplorerShareableUrlSearchParam = "request"

const apiExplorerLocalStorageKey = "prismic-api-explorer"

function getInitialRequestProperties(
  repository: APIExplorerProviderProps["repository"],
): APIExplorerRequestProperties {
  // Retrieve state from shareable URL if available first
  const url = new URL(window.location.href)
  if (url.searchParams.has(apiExplorerShareableUrlSearchParam)) {
    try {
      const parsedState: unknown = JSON.parse(
        url.searchParams.get(apiExplorerShareableUrlSearchParam) ?? "",
      )
      const validatedState = APIExplorerRequestProperties.safeParse(parsedState)

      if (validatedState.success) {
        // Remove state from URL so that refreshing the page uses the local storage state
        // instead of reapplying the URL state.
        url.searchParams.delete(apiExplorerShareableUrlSearchParam)
        window.history.replaceState({}, "", url.toString())

        // Update local storage with the new state
        localStorage.write(
          apiExplorerLocalStorageKey,
          JSON.stringify(validatedState.data),
        )

        return validatedState.data
      } else {
        console.warn(
          "Could not retrieve API Explorer state from URL",
          validatedState.error,
        )
      }
    } catch (error) {
      console.warn("Could not parse API Explorer state from URL", error)
    }
  }

  // Retrieve previous state from local storage if available.
  if (localStorage.has(apiExplorerLocalStorageKey)) {
    const storedState = localStorage.readJSON(
      apiExplorerLocalStorageKey,
      APIExplorerRequestProperties,
    )

    if (storedState) {
      return storedState
    } else {
      console.warn("Could not retrieve API Explorer state from local storage")
    }
  }

  return {
    repository: {
      id: repository?.id ?? "",
    },
    type: "get",
    options: {},
    pagination: {},
  }
}
